/**
 * Component: Quick Add
 * ------------------------------------------------------------------------------
 * A quick add file that contains scripts to function.
 *
 * @namespace quickView
 */
import $ from 'jquery';
import Variants from '@shopify/theme-variants';
import {formatMoney} from '@shopify/theme-currency';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-quick-add="container"]',
  addToCart: '[js-quick-add="addToCart"]',
  addToCartText: '[data-add-to-cart-text]',
  originalSelectorId: '[data-product-select]',
  productJson: '[data-product-json]',
  singleOptionSelector: '[data-single-option-selector]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  priceWrapper: '[data-price-wrapper]',
  productPrice: '[data-product-price]',
};

const cssClasses = {
  activeThumbnail: 'active-thumbnail',
  hide: 'hide',
};

/**
 * Create a new quick add object.
 * @param {object} config - Settings object defined when instantiating.
 */
export default () => {
  // DOM Node Selectors
  const nodeSelectors = {
    container: document.querySelectorAll(selectors.container),
    addToCart: document.querySelectorAll(selectors.addToCart),
  };

  // Init component
  function init() {
    setTriggerEvents();
    setDeaultSelect();
  }

  // Set trigger listeners
  function setTriggerEvents() {
    [...nodeSelectors.container].forEach((item) => {
      createSelector(item);
    });
  }

  function createSelector(item) {
    const $item = $(item);
    const productSingleObject = JSON.parse(
      $(item).find(selectors.productJson).html(),
    );

    const options = {
      $container: $item,
      enableHistoryState: false,
      singleOptionSelector: $item.find(selectors.singleOptionSelector),
      originalSelectorId: $item.find(selectors.originalSelectorId),
      product: productSingleObject,
    };

    new Variants(options);

    $item.on('variantChange', updateAddToCartState.bind(item, $item));
    $item.on('variantPriceChange', updateProductPrices.bind(item, $item));
  }

  function setDeaultSelect() {
    [...nodeSelectors.container].forEach((item) => {
      const label = item.querySelector('[for=SingleOptionSelector-0]');
      const labelTwo = item.querySelector('[for=SingleOptionSelector-1]');

      if (label != null) {
        triggerSelectUpdate(
          item,
          '[for=SingleOptionSelector-0]',
          '#SingleOptionSelector-0',
        );
      }

      if (labelTwo != null) {
        triggerSelectUpdate(
          item,
          '[for=SingleOptionSelector-1]',
          '#SingleOptionSelector-1',
        );
      }
    });
  }

  function triggerSelectUpdate(item, label, select) {
    const labelValue = item.querySelector(label).textContent;
    const selectNode = item.querySelector(select);
    const option = document.createElement('option');
    option.text = labelValue;
    option.value = labelValue;
    selectNode.add(option, selectNode[0]);
    selectNode.selectedIndex = '0';
    // Create a new 'change' event
    const event = new Event('change');

    // Dispatch it.
    selectNode.dispatchEvent(event);

    // For article quick add components, make active on load.
    const articleQuickAdd = $('.article__quick-add .karmoon--btn-select');
    if (articleQuickAdd) {
      articleQuickAdd.removeClass('not-value');
    }
  }

  function updateAddToCartState(target, item) {
    const $target = $(target);
    const {variant} = item;

    let optionSelector;
    const shadeSelect = $target[0].querySelector('select[data-shade="true"]');

    if (shadeSelect) {
      optionSelector = shadeSelect.nextElementSibling;
    } else {
      optionSelector = $target[0].querySelector('.karmoon-option-select');
    }

    if (variant && optionSelector) {
      Karmoon.EventBus.emit('AddToCart:variantSelected', {
        selector: optionSelector,
        state: true,
      });
    } else if (optionSelector) {
      Karmoon.EventBus.emit('AddToCart:variantSelected', {
        selector: optionSelector,
        state: false,
      });
      return;
    } else {
      $(selectors.addToCart, $target).prop('disabled', true);
      $(selectors.priceWrapper, $target).addClass(cssClasses.hide);
      $(selectors.addToCartText, $target).html(theme.strings.unavailable);
      return;
    }

    if (variant.available && optionSelector) {
      optionSelector.classList.remove('highlighted');
    }

    if (variant.available) {
      $(selectors.addToCart, $target).prop('disabled', false);
      $(selectors.addToCart, $target).removeClass('sold-out');
      $(selectors.addToCartText, $target).html(theme.strings.addToCart);
    } else {
      $(selectors.addToCart, $target).prop('disabled', true);
      $(selectors.addToCart, $target).addClass('sold-out');
      $(selectors.addToCartText, $target).html(theme.strings.soldOut);
    }
  }

  /**
   * Updates the DOM with specified prices
   *
   * @param {string} productPrice - The current price of the product
   * @param {string} comparePrice - The original price of the product
   */
  function updateProductPrices(target, item) {
    const $target = $(target);
    const {variant} = item;
    const $comparePrice = $(selectors.comparePrice, $target);
    const $compareEls = $comparePrice.add(selectors.comparePriceText, $target);

    $(selectors.productPrice, $target).html(
      formatMoney(variant.price, theme.moneyFormat),
    );

    if (variant.compare_at_price > variant.price) {
      $comparePrice.html(
        `Was ${formatMoney(variant.compare_at_price, theme.moneyFormat)}`,
      );
      $compareEls.removeClass(cssClasses.hide);
    } else {
      $comparePrice.html('');
      $compareEls.addClass(cssClasses.hide);
    }
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
